"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";

import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@workspace/ui/components/carousel";
import { useEmblaHorizontalScrollAnalytics } from "@/components/analytics/use-embla-horizontal-scroll-analytics";
import {
  EventNames,
  SectionTypes,
  sendEvent,
  withSectionType,
} from "@/lib/utils/analytics";

export type UniqueCycleCardData = {
  badge: string;
  image: string;
  imagePosition?: string;
  flipImage?: boolean;
  title: string;
  href: string;
  overlay:
    | "pcos"
    | "trying-after-35"
    | "after-miscarriage"
    | "irregular-cycles"
    | "pre-ivf";
};

export type UniqueCycleSectionProps = {
  title: string;
  description?: string;
  cards: UniqueCycleCardData[];
};

function UniqueCycleProgress({ api }: { api: CarouselApi | undefined }) {
  const [count, setCount] = React.useState(0);
  const trackRef = React.useRef<HTMLDivElement>(null);
  const thumbRef = React.useRef<HTMLDivElement>(null);
  const thumbWidth = 41;

  const syncIndicator = React.useCallback(() => {
    if (!api) return;

    setCount(api.scrollSnapList().length);

    const track = trackRef.current;
    const thumb = thumbRef.current;

    if (!track || !thumb) return;

    const rawProgress = api.scrollProgress();
    const progress = Number.isFinite(rawProgress)
      ? Math.max(0, Math.min(1, rawProgress))
      : 0;
    const maxOffset = Math.max(0, track.clientWidth - thumbWidth);

    thumb.style.transform = `translate3d(${(maxOffset * progress).toFixed(
      2,
    )}px, 0, 0)`;
  }, [api]);

  React.useEffect(() => {
    if (!api) return;

    syncIndicator();
    api.on("reInit", syncIndicator);
    api.on("select", syncIndicator);
    api.on("scroll", syncIndicator);

    return () => {
      api.off("reInit", syncIndicator);
      api.off("select", syncIndicator);
      api.off("scroll", syncIndicator);
    };
  }, [api, syncIndicator]);

  React.useEffect(() => {
    const track = trackRef.current;
    if (!track || !api) return;

    const observer = new ResizeObserver(() => {
      syncIndicator();
    });

    observer.observe(track);

    return () => {
      observer.disconnect();
    };
  }, [api, syncIndicator]);

  if (count <= 1) return null;

  return (
    <div
      id="use-case-indicator"
      data-testid="use-case-indicator"
      className="w-full px-6 lg:hidden"
    >
      <div
        ref={trackRef}
        id="use-case-indicator-track"
        data-testid="use-case-indicator-track"
        className="relative h-[3px] w-full overflow-hidden rounded-full bg-[#F3F2F1]"
      >
        <div
          ref={thumbRef}
          id="use-case-indicator-thumb"
          data-testid="use-case-indicator-thumb"
          className="absolute left-0 top-0 h-full w-[41px] rounded-full bg-primary will-change-transform"
        />
      </div>
    </div>
  );
}

function CycleTargetIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 27 27"
      fill="none"
      aria-hidden="true"
      className={cn("size-[27px]", className)}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.7219 15.5893C13.7219 13.9738 12.4123 12.6648 10.7975 12.6648C9.1826 12.6648 7.87305 13.9738 7.87305 15.5893C7.87305 17.2041 9.1826 18.5137 10.7975 18.5137C12.4123 18.5137 13.7219 17.2041 13.7219 15.5893Z"
        fill="currentColor"
      />
      <circle
        cx="9.61078"
        cy="9.61078"
        r="8.98204"
        transform="matrix(1 0 0 -1 3.50342 22.7227)"
        stroke="currentColor"
        strokeWidth="1.25748"
      />
      <circle
        cx="12.5749"
        cy="12.5749"
        r="12.5749"
        transform="matrix(1 0 0 -1 0.628906 25.7793)"
        stroke="currentColor"
        strokeWidth="1.25748"
        strokeLinecap="round"
        strokeDasharray="4.31 4.31"
      />
    </svg>
  );
}

function HeartRiseIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 20 32"
      fill="none"
      aria-hidden="true"
      className={cn("h-[32px] w-[20px]", className)}
    >
      <path
        d="M2.48884 3.19873L4.27482 5.1385M16.8301 3.19873L15.0441 5.1385M9.65959 0.541016V3.94509M16.8301 27.8833L15.0441 25.9436M2.48884 27.8833L4.27482 25.9436M9.65959 30.5411V27.137M11.8249 9.6938C13.3794 8.04859 15.941 8.00297 17.5388 9.58787C19.1525 11.158 19.1972 13.7697 17.6414 15.415L10.3307 23.1484C9.96646 23.5334 9.36191 23.534 8.99767 23.1491L1.67256 15.415C0.118703 13.7697 0.177788 11.158 1.77631 9.58787C3.37483 8.00295 5.93534 8.04855 7.49045 9.6938L9.35879 11.6657C9.38012 11.6989 9.4046 11.7306 9.43259 11.7601L10.8066 13.2103L10.865 13.2647C11.1085 13.4646 11.4656 13.453 11.6962 13.2257C11.946 12.9794 11.9528 12.5732 11.7114 12.3184L10.5288 11.0702L11.8249 9.6938Z"
        stroke="currentColor"
        strokeWidth="1.08297"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function OvulationConfirmedIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 25 28"
      fill="none"
      aria-hidden="true"
      className={cn("h-[28px] w-[25px]", className)}
    >
      <path
        d="M5.38143 19.8205C2.41516 17.9694 0.441406 14.6765 0.441406 10.9226C0.441406 5.1338 5.13428 0.441406 10.9226 0.441406C16.7114 0.441406 21.4038 5.1338 21.4038 10.9226"
        stroke="currentColor"
        strokeWidth="0.882792"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="3.53 3.53"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.3609 8.92116C11.3609 10.2684 10.2688 11.3599 8.92214 11.3599C7.57546 11.3599 6.4834 10.2684 6.4834 8.92116C6.4834 7.57449 7.57546 6.48242 8.92214 6.48242C10.2688 6.48242 11.3609 7.57449 11.3609 8.92116Z"
        fill="currentColor"
      />
      <path
        d="M8.07079 18.3414C8.29832 18.4289 8.55371 18.3154 8.64123 18.0879C8.72875 17.8604 8.61526 17.605 8.38774 17.5175L8.22927 17.9294L8.07079 18.3414ZM14.3234 17.1157C14.1099 17.2332 14.032 17.5016 14.1495 17.7151C14.267 17.9287 14.5354 18.0066 14.7489 17.8891L14.5362 17.5024L14.3234 17.1157ZM23.4726 18.7255L23.8083 19.0121L23.8108 19.0091L23.4726 18.7255ZM10.0725 21.6408L9.73929 21.9304L9.73939 21.9305L10.0725 21.6408ZM10.2753 18.6915L10.5659 19.0237L10.5662 19.0234L10.2753 18.6915ZM13.2246 18.8943L12.8913 19.1837L12.8922 19.1847L13.2246 18.8943ZM15.5585 21.5664L15.226 21.8568C15.3104 21.9534 15.4327 22.0085 15.561 22.0078C15.6893 22.007 15.811 21.9505 15.8943 21.8529L15.5585 21.5664ZM20.2729 16.04L20.6088 16.3264L20.6121 16.3224L20.2729 16.04ZM23.2159 15.7832L22.932 16.1212L22.9336 16.1225L23.2159 15.7832ZM16.9088 26.4138L17.2445 26.7004L16.9088 26.4138ZM14.2337 26.426L13.9007 26.7156L14.2337 26.426ZM3.41699 10.921H3.85839C3.85839 7.01887 7.02136 3.85546 10.9234 3.85546V3.41406V2.97267C6.53373 2.97267 2.9756 6.53139 2.9756 10.921H3.41699ZM10.9234 3.41406V3.85546C14.8255 3.85546 17.9884 7.01887 17.9884 10.921H18.4298H18.8712C18.8712 6.53139 15.3131 2.97267 10.9234 2.97267V3.41406ZM8.22927 17.9294L8.38774 17.5175C5.73771 16.4981 3.85839 13.9283 3.85839 10.921H3.41699H2.9756C2.9756 14.3055 5.0912 17.1952 8.07079 18.3414L8.22927 17.9294ZM18.4298 10.921H17.9884C17.9884 13.5895 16.509 15.9133 14.3234 17.1157L14.5362 17.5024L14.7489 17.8891C17.2052 16.5379 18.8712 13.9242 18.8712 10.921H18.4298ZM23.4726 18.7255L23.1369 18.4389L16.5731 26.1272L16.9088 26.4138L17.2445 26.7004L23.8083 19.0121L23.4726 18.7255ZM14.2337 26.426L14.5668 26.1363L10.4055 21.3512L10.0725 21.6408L9.73939 21.9305L13.9007 26.7156L14.2337 26.426ZM10.0725 21.6408L10.4056 21.3513C9.80783 20.6634 9.88227 19.6217 10.5659 19.0237L10.2753 18.6915L9.98467 18.3593C8.93655 19.2761 8.821 20.8737 9.73929 21.9304L10.0725 21.6408ZM10.2753 18.6915L10.5662 19.0234C11.2536 18.421 12.2934 18.4951 12.8913 19.1837L13.2246 18.8943L13.5579 18.6049C12.641 17.549 11.0416 17.433 9.98436 18.3595L10.2753 18.6915ZM13.2246 18.8943L12.8922 19.1847L15.226 21.8568L15.5585 21.5664L15.8909 21.276L13.5571 18.604L13.2246 18.8943ZM15.5585 21.5664L15.8943 21.8529L20.6087 16.3264L20.2729 16.04L19.9371 15.7535L15.2226 21.2799L15.5585 21.5664ZM20.2729 16.04L20.6121 16.3224C21.1922 15.6257 22.2316 15.5329 22.932 16.1212L23.2159 15.7832L23.4998 15.4452C22.4273 14.5444 20.8286 14.6827 19.9337 15.7575L20.2729 16.04ZM23.2159 15.7832L22.9336 16.1225C23.6298 16.7017 23.7226 17.7405 23.1344 18.4419L23.4726 18.7255L23.8108 19.0091C24.7105 17.9364 24.5735 16.3384 23.4982 15.4439L23.2159 15.7832ZM16.9088 26.4138L16.5731 26.1272C16.0476 26.7427 15.0979 26.747 14.5668 26.1363L14.2337 26.426L13.9007 26.7156C14.7858 27.7334 16.3687 27.7262 17.2445 26.7004L16.9088 26.4138Z"
        fill="currentColor"
      />
    </svg>
  );
}

function FertilityWindowIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 30 30"
      fill="none"
      aria-hidden="true"
      className={cn("size-[30px]", className)}
    >
      <path
        d="M13.533 19.7161C12.7528 19.196 10.1521 17.3755 10.1521 15.555C10.1521 14.5147 10.6723 13.7345 11.7125 13.7345C12.4927 13.7345 13.273 14.2547 13.533 14.7748C13.7931 14.2547 14.5733 13.7345 15.3535 13.7345C16.3938 13.7345 16.9139 14.5147 16.9139 15.555C16.9139 17.3755 14.3132 19.196 13.533 19.7161ZM13.533 20.7564C13.7931 20.4963 17.6941 18.1557 17.6941 15.555C17.6941 14.2547 16.9139 12.9543 15.3535 12.9543C14.5733 12.9543 14.0532 13.2144 13.533 13.7345C13.0129 13.2144 12.4927 12.9543 11.7125 12.9543C10.1521 12.9543 9.37191 14.2547 9.37191 15.555C9.37191 18.1557 13.273 20.4963 13.533 20.7564ZM18.4744 3.5918H16.3938C15.8737 3.5918 15.3535 4.11194 15.3535 4.63208V6.45256H16.1337V4.37201H18.7344V9.05326H19.5146V4.63208C19.5146 4.11194 18.9945 3.5918 18.4744 3.5918ZM10.1521 6.9727H17.9542V6.19249H10.1521V6.9727ZM4.17052 10.0935V10.8738H23.1556V10.0935H4.17052ZM9.89205 3.5918H7.81149C7.29135 3.5918 6.77121 4.11194 6.77121 4.63208V6.45256H7.55142V4.37201H10.1521V9.05326H10.9323V4.63208C10.9323 4.11194 10.4122 3.5918 9.89205 3.5918Z"
        fill="currentColor"
      />
      <path
        d="M7.34441 7.41755V8.28477H2.8433V25.2586H20.9679V26.1258H1.97607V7.41755H7.34441Z"
        fill="currentColor"
      />
      <path
        d="M23.3834 7.41755V15.5275H22.5162V8.28477H21.2207V7.41755H23.3834Z"
        fill="currentColor"
      />
      <path
        d="M22.6063 16.9671C22.6063 16.7075 22.8167 16.4971 23.0762 16.4971C23.3358 16.4971 23.5462 16.7075 23.5462 16.9671V26.2545H22.6063V16.9671Z"
        fill="currentColor"
      />
      <path
        d="M22.7764 16.565C22.961 16.4145 23.2334 16.4255 23.4055 16.5976L28.1651 21.3571L27.5002 22.0215L23.0728 17.594L18.6459 22.0215L17.9811 21.3571L22.7407 16.5976L22.7764 16.565Z"
        fill="currentColor"
      />
    </svg>
  );
}

function TrendArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
      className={cn("size-[18px]", className)}
    >
      <path
        d="m3.3 11.7 4.1-4.1 2.2 2.2 5.1-5.1"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.4 4.7h3.35v3.35"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function OverlayGlassCard({
  title,
  description,
  icon,
  className,
  textClassName,
  index,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  className?: string;
  textClassName?: string;
  index: number;
}) {
  return (
    <div
      className={cn(
        "rounded-[10px] px-[13px] py-[10px] backdrop-blur-md",
        className,
      )}
    >
      <div className={cn("flex items-center gap-[10px]", textClassName)}>
        <div className="shrink-0">{icon}</div>
        <p
          id={`use-case-overlay-title-${index}`}
          data-testid={`use-case-overlay-title-${index}`}
          className="font-[family-name:var(--font-resist-text)] text-[18px] font-normal leading-[1.1] tracking-[-0.01em] lg:text-[20px]"
        >
          {title}
        </p>
      </div>
      <p
        id={`use-case-overlay-description-${index}`}
        data-testid={`use-case-overlay-description-${index}`}
        className={cn(
          "mt-[10px] max-w-[210px] font-[family-name:var(--font-office-times-sharp)] text-[12px] leading-[1.1] tracking-[-0.01em] lg:text-[14px]",
          textClassName,
        )}
      >
        {description}
      </p>
    </div>
  );
}

function AxisLabels({
  labels,
  activeLabel,
  className,
}: {
  labels: string[];
  activeLabel: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-end justify-between px-2 text-[11px] leading-none",
        className,
      )}
    >
      {labels.map((label) => (
        <span
          key={label}
          className={cn(
            "font-[family-name:var(--font-inter)] font-medium text-white/55",
            label === activeLabel && "text-[#B068FF]",
          )}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function UniqueCycleOverlay({
  overlay,
  index,
}: {
  overlay: UniqueCycleCardData["overlay"];
  index: number;
}) {
  switch (overlay) {
    case "pcos":
      return (
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-x-0 bottom-0 h-[160px] bg-gradient-to-t from-black/30 to-transparent" />
          <OverlayGlassCard
            title="Ovulation unlikely"
            description="Your PdG has not risen steadily or consistently this cycle."
            icon={<CycleTargetIcon />}
            className="absolute left-[20px] top-[300px] w-[254px] bg-white/20 lg:left-7 lg:top-[355px]"
            textClassName="text-white"
            index={index}
          />
        </div>
      );
    case "trying-after-35":
      return (
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-x-0 bottom-0 h-[170px] backdrop-blur-[3px] bg-[linear-gradient(to_bottom,rgba(0,0,0,0.005),rgba(56,56,27,0.5))] lg:h-[220px]" />
          <OverlayGlassCard
            title="Small LH rise detected"
            description="You have a small LH rise. This may mean ovulation."
            icon={<HeartRiseIcon />}
            className="absolute left-[20px] top-[240px] w-[269px] bg-black/30 lg:left-[24px] lg:top-[300px]"
            textClassName="text-white"
            index={index}
          />
          <svg
            viewBox="0 0 360 132"
            className="absolute inset-x-0 bottom-[6px] h-[110px] w-full lg:bottom-[10px] lg:h-[120px]"
            aria-hidden="true"
          >
            <line
              className="lg:hidden"
              x1="160"
              y1="24"
              x2="160"
              y2="42"
              stroke="#FFFFFF"
              strokeWidth="1.4"
              opacity=".72"
            />
            <line
              className="hidden lg:block"
              x1="158"
              y1="6"
              x2="158"
              y2="42"
              stroke="#FFFFFF"
              strokeWidth="1.4"
              opacity=".72"
            />
            <path
              d="M-8 102C34 102 68 102 90 102C108 102 119 95 126 76C134 55 147 41 163 41C180 41 197 63 211 84C225 105 251 110 298 106C326 104 347 104 372 106"
              fill="none"
              stroke="#B068FF"
              strokeWidth="2.6"
              strokeLinecap="round"
            />
          </svg>
          <AxisLabels
            labels={[
              "9",
              "10",
              "11",
              "12",
              "13",
              "14",
              "15",
              "16",
              "17",
              "18",
              "19",
            ]}
            activeLabel="13"
            className="absolute inset-x-[10px] bottom-[8px] lg:bottom-[10px]"
          />
        </div>
      );
    case "after-miscarriage":
      return (
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-x-0 bottom-0 h-[175px] bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
          <OverlayGlassCard
            title="Ovulation confirmed"
            description="A steady, consistent rise in PdG was detected."
            icon={<OvulationConfirmedIcon />}
            className="absolute left-[14px] top-[253px] w-[254px] bg-black/35 lg:top-[336px]"
            textClassName="text-white"
            index={index}
          />
          <div className="absolute left-[14px] top-[363px] rounded-[10px] bg-[#5790FF] px-[18px] py-[11px] text-center backdrop-blur-md lg:top-[446px]">
            <p
              id={`use-case-overlay-text-${index}`}
              data-testid={`use-case-overlay-text-${index}`}
              className="font-[family-name:var(--font-resist-text)] text-[15px] font-normal leading-[1.1] tracking-[-0.01em] text-white"
            >
              You have started ovulating again.
            </p>
          </div>
        </div>
      );
    case "irregular-cycles":
      return (
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-x-0 bottom-0 h-[170px] bg-gradient-to-t from-black/35 via-black/10 to-transparent" />
          <div className="absolute right-8 top-[219px] flex items-center gap-2 rounded-full bg-[#A55CF3] px-4 py-2 text-white shadow-[0_10px_28px_rgba(129,64,238,0.28)] lg:left-[120px] lg:right-auto lg:top-[306px]">
            <TrendArrowIcon />
            <span
              id={`use-case-overlay-text-${index}`}
              data-testid={`use-case-overlay-text-${index}`}
              className="whitespace-nowrap font-[family-name:var(--font-resist-text)] text-[14px] leading-[1.1] tracking-[-0.01em] lg:text-[15px]"
            >
              Early LH rise detected
            </span>
          </div>
          <OverlayGlassCard
            title="High Fertility"
            description="Your fertile window has started. Have sex today."
            icon={<FertilityWindowIcon />}
            className="absolute left-[20px] top-[304px] w-[254px] bg-white/30 lg:left-[19px] lg:top-[387px]"
            textClassName="text-white"
            index={index}
          />
        </div>
      );
    case "pre-ivf":
      return (
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-x-0 bottom-0 h-[195px] bg-black/[0.01] backdrop-blur-[3px] lg:h-[239px]" />
          <div className="absolute inset-x-0 bottom-0 h-[185px] bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
          <div className="absolute left-[26px] top-[214px] rounded-[10px] bg-[#31353E]/88 px-5 py-3 text-white backdrop-blur-md lg:left-[40px] lg:top-[289px]">
            <div className="grid grid-cols-4 gap-5">
              {[
                ["E3G", "158.4", "#4F8A4D"],
                ["PdG", "1.1", "#5790FF"],
                ["LH", "28.2", "#B36EFF"],
                ["FSH", "7.5", "#FF7D29"],
              ].map(([label, value, color], metricIndex) => (
                <div key={label} className="flex flex-col gap-1">
                  <span
                    id={`use-case-overlay-metric-${metricIndex + 1}-label`}
                    data-testid={`use-case-overlay-metric-${metricIndex + 1}-label`}
                    className="font-[family-name:var(--font-inter)] text-[10px] font-semibold leading-none lg:text-[12px]"
                    style={{ color }}
                  >
                    {label}
                  </span>
                  <span
                    id={`use-case-overlay-metric-${metricIndex + 1}-value`}
                    data-testid={`use-case-overlay-metric-${metricIndex + 1}-value`}
                    className="font-[family-name:var(--font-resist-text)] text-[14px] leading-none lg:text-[16px]"
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <svg
            viewBox="0 0 360 152"
            className="absolute inset-x-0 bottom-[24px] h-[152px] w-full lg:bottom-[16px]"
            aria-hidden="true"
          >
            <line
              x1="172"
              y1="8"
              x2="172"
              y2="143"
              stroke="#FFFFFF"
              strokeWidth="1.3"
              opacity=".7"
            />
            <g transform="translate(0 80)">
              <path
                d="M-3.91162 60.7525C10.2284 60.3633 39.8325 60.6148 65.5 40.328C88.9068 21.828 106 -0.669922 136.543 0.568423C165.123 1.72721 176.699 35.13 199.5 38.0013C216.776 40.1768 230.303 28.5243 243.956 29.5786C259.19 30.755 271.446 46.885 290.842 53.9109C330.771 68.3745 371.712 63.467 399.282 62.0505"
                fill="none"
                stroke="#45804F"
                strokeWidth="1.0379"
              />
            </g>
            <g transform="translate(0 54)">
              <path
                d="M388 69.1149C388 69.1149 360.78 57.6324 340.107 57.8372C319.7 58.0393 305.946 61.3456 283.246 69.1149C262.261 76.2967 249.387 82.3295 227.788 69.1149C205.952 55.7562 194.224 -0.504785 172.33 0.513652C151.23 1.49519 139.965 55.5053 118.927 69.1149C97.3332 83.0842 62.8141 67.744 26.8835 69.1149C6.82235 69.8803 -24.4565 72.1684 -24.4565 72.1684"
                fill="none"
                stroke="#C188FF"
                strokeWidth="1.0379"
              />
            </g>
            <g transform="translate(0 106)">
              <path
                d="M-36.5 32.082C-21.7693 32.899 33.0434 33.5116 47.5 30.5672C73.382 25.2956 89.7694 2.74012 110.221 0.761514C138.835 -2.00677 152.302 19.8695 181.031 20.8765C199.652 21.5291 209.781 14.2895 228.395 15.1299C249.061 16.063 259.588 23.6911 279.978 27.1792C325.928 35.0396 368.95 31.9114 399.282 31.2099"
                fill="none"
                stroke="#FF5700"
                strokeWidth="1.0379"
              />
            </g>
            <g transform="translate(0 78)">
              <path
                d="M-8.0127 67.0055C24.9882 67.0055 97 68.4297 164 65.4297C195.809 64.0054 214.041 60.6647 244.08 50.1059C280.725 37.2252 294.974 3.61667 330.394 0.681316C370.01 -2.60179 387.458 39.7641 427.165 41.6586C436.505 42.1042 441.851 42.9798 451.107 41.6586C472.537 38.6 478.753 16.3331 500.399 16.3064C512.941 16.2909 520.586 18.3764 531.383 24.7585"
                fill="none"
                stroke="#5790FF"
                strokeWidth="1.0379"
              />
            </g>
          </svg>
          <AxisLabels
            labels={[
              "11",
              "12",
              "13",
              "14",
              "15",
              "16",
              "17",
              "18",
              "19",
              "20",
              "21",
            ]}
            activeLabel="16"
            className="absolute inset-x-[10px] bottom-[14px] lg:bottom-[6px]"
          />
        </div>
      );
  }
}

function UniqueCycleCard({
  badge,
  image,
  imagePosition,
  flipImage,
  overlay,
  title,
  href,
  index,
}: UniqueCycleCardData & { index: number }) {
  const useCaseValue = overlay.replace(/-/g, "_");

  return (
    <article className="w-full max-w-[300px] xs:max-w-[330px] lg:max-w-[360px] shrink-0">
      <Link
        href={href}
        id={`use-case-link-${index}`}
        data-testid={`use-case-link-${index}`}
        className="flex flex-col"
        onClick={() =>
          sendEvent(
            EventNames.USE_CASE_CLICKED,
            withSectionType(SectionTypes.USE_CASE, {
              use_case: useCaseValue,
            }),
          )
        }
      >
        <div className="relative h-[424px] lg:h-[507px] w-full overflow-hidden rounded-2xl cursor-pointer">
          <Image
            src={image}
            alt={title}
            className={cn("object-cover", flipImage && "-scale-x-100")}
            style={
              imagePosition ? { objectPosition: imagePosition } : undefined
            }
            fill
            sizes="(min-width: 1024px) 400px, (min-width: 640px) 360px, 100vw"
          />
          <UniqueCycleOverlay overlay={overlay} index={index} />
          <Badge
            variant="tag"
            id={`use-case-badge-${index}`}
            data-testid={`use-case-badge-${index}`}
            className="absolute left-5 top-5 z-10 rounded-[6px] px-2 py-1.5 text-[12px] leading-4 lg:text-[14px]"
          >
            {badge}
          </Badge>
        </div>

        <div className="mt-6 flex flex-col items-start gap-6">
          <p
            id={`use-case-title-${index}`}
            data-testid={`use-case-title-${index}`}
            className="title-20 text-primary"
          >
            {title}
          </p>
          <Button
            variant="secondary"
            size="xs"
            asChild
            id={`use-case-cta-${index}`}
            data-testid={`use-case-cta-${index}`}
          >
            <span id={`use-case-cta-${index}-text`} data-testid={`use-case-cta-${index}-text`}>
              Learn more
            </span>
          </Button>
        </div>
      </Link>
    </article>
  );
}

export function UniqueCycleSection({
  title,
  description,
  cards,
}: UniqueCycleSectionProps) {
  const [api, setApi] = React.useState<CarouselApi>();
  useEmblaHorizontalScrollAnalytics({
    api,
    sectionType: SectionTypes.USE_CASE,
  });

  return (
    <section
      id="use-case"
      data-section-type="use_case"
      className="bg-base-1 py-20 lg:bg-transparent lg:px-20 lg:py-[120px]"
    >
      <div className="mx-auto w-full max-w-[1440px]">
        <Carousel
          id="use-case-hscroll"
          data-testid="use-case-hscroll"
          setApi={setApi}
          opts={{
            align: "start",
            containScroll: "trimSnaps",
            slidesToScroll: 1,
          }}
        >
          <div className="flex items-end justify-between gap-8 px-6 lg:px-0">
            <div className="flex flex-col gap-5">
              <h2
                id="use-case-title"
                data-testid="use-case-title"
                className="title-32 text-primary lg:title-40 lg:max-w-[484px]"
              >
                {title}
              </h2>
              {description ? (
                <p
                  id="use-case-description"
                  data-testid="use-case-description"
                  className="body-18 text-primary lg:max-w-[642px] lg:text-secondary"
                >
                  {description}
                </p>
              ) : null}
            </div>
            <div className="hidden items-center gap-3 lg:flex">
              <CarouselPrevious
                variant="outline"
                id="use-case-nav-prev"
                data-testid="use-case-nav-prev"
                className="static size-12 translate-y-0 rounded-full border border-base-7 text-base-7 disabled:opacity-30"
              />
              <CarouselNext
                variant="outline"
                id="use-case-nav-next"
                data-testid="use-case-nav-next"
                className="static size-12 translate-y-0 rounded-full border border-primary text-primary disabled:opacity-30"
              />
            </div>
          </div>
          <div className="flex flex-col gap-6 lg:gap-10">
            <CarouselContent
              viewportClassName="px-6 lg:px-0"
              className="ml-0 mt-8 gap-5 lg:mt-12"
            >
              {cards.map((card, index) => (
                <CarouselItem
                  key={card.title}
                  id={`use-case-slide-${index + 1}`}
                  data-testid={`use-case-slide-${index + 1}`}
                  className="shrink-0 grow-0 lg:basis-[min(360px,100%)] basis-[min(300px,100%)] xs:basis-[min(330px,100%)] pl-0 pr-0"
                >
                  <UniqueCycleCard
                    badge={card.badge}
                    image={card.image}
                    imagePosition={card.imagePosition}
                    flipImage={card.flipImage}
                    overlay={card.overlay}
                    title={card.title}
                    href={card.href}
                    index={index + 1}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>

            <UniqueCycleProgress api={api} />
          </div>
        </Carousel>
      </div>
    </section>
  );
}
